import { useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import Header from "@/components/Header";
import api from "@/utils/api";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend,
  ComposedChart, Line, CartesianGrid, ScatterChart, Scatter, ZAxis, ReferenceLine,
} from "recharts";
import {
  ArrowLeft, CheckCircle2, XCircle, Zap, Settings, Clock,
  Package, User, Wrench, Activity, Layers, Radio,
} from "lucide-react";

const todayIso = () => {
  const d = new Date();
  const off = d.getTimezoneOffset() * 60000;
  return new Date(d.getTime() - off).toISOString().slice(0, 10);
};

const MachineDeepDive = () => {
  const { machineCode } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialDate = searchParams.get("date") || todayIso();
  const [date, setDate] = useState(initialDate);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [mountState, setMountState] = useState(null);
  const [mountEvents, setMountEvents] = useState([]);
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [toast, setToast] = useState("");
  const [strokeWindow, setStrokeWindow] = useState("shift"); // shift | 1h | 24h | 7d
  const [strokeEvents, setStrokeEvents] = useState([]);
  const [strokeMeta, setStrokeMeta] = useState({ count: 0, truncated: false, start_time: null, end_time: null });
  const [strokeLoading, setStrokeLoading] = useState(false);

  const fetchStrokeEvents = () => {
    setStrokeLoading(true);
    api
      .get(`/machine-deep-dive/stroke-events?machine_code=${machineCode}&window=${strokeWindow}`)
      .then((r) => {
        setStrokeEvents(r.data?.events || []);
        setStrokeMeta({
          count: r.data?.count || 0,
          truncated: !!r.data?.truncated,
          start_time: r.data?.start_time,
          end_time: r.data?.end_time,
        });
      })
      .catch(() => {
        setStrokeEvents([]);
        setStrokeMeta({ count: 0, truncated: false, start_time: null, end_time: null });
      })
      .finally(() => setStrokeLoading(false));
  };

  const fetchMount = () => {
    api.get(`/tool-mount/state/${machineCode}`).then((r) => setMountState(r.data)).catch(() => setMountState(null));
    api.get(`/tool-mount/events?machine_code=${machineCode}&limit=30`).then((r) => setMountEvents(r.data || [])).catch(() => setMountEvents([]));
  };

  useEffect(() => {
    setLoading(true);
    setError("");
    api.get(`/machine-deep-dive?machine_code=${machineCode}&date=${date}`)
      .then((r) => setData(r.data))
      .catch((e) => setError(e.response?.data?.detail || "Failed to load"))
      .finally(() => setLoading(false));
    fetchMount();
    fetchStrokeEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [machineCode, date]);

  useEffect(() => {
    fetchStrokeEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [strokeWindow, machineCode]);

  const rejPct = data?.totals?.rejection_pct || 0;

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      <div className="max-w-7xl mx-auto p-6">
        <button
          onClick={() => navigate(-1)}
          className="text-sm text-slate-500 hover:text-slate-800 inline-flex items-center gap-1 mb-4"
        >
          <ArrowLeft size={14} /> Back
        </button>

        {/* Header banner */}
        <div className="bg-gradient-to-r from-[#1A3560] to-[#2d4a7d] text-white rounded-2xl p-6 shadow-lg mb-4">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <p className="text-xs font-bold uppercase tracking-widest opacity-80">Machine Deep-Dive</p>
              <h1 className="text-3xl font-extrabold inline-flex items-center gap-2">
                <Package size={24} /> {machineCode}
                {data?.tonnage && (
                  <span className="text-lg opacity-70">· {data.tonnage}T</span>
                )}
              </h1>
              <p className="text-sm opacity-90">Hourly trend for {new Date(date).toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}</p>
            </div>
            <input
              data-testid="deepdive-date"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="bg-white/10 border border-white/30 rounded-lg px-3 py-2 text-white text-sm"
            />
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-800 mb-4">
            {error}
          </div>
        )}

        {toast && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-3 text-sm text-emerald-800 mb-4">
            {toast}
          </div>
        )}

        {/* Tool mount state banner */}
        {mountState && mountState.mounted ? (
          <div
            data-testid="mount-banner"
            className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 mb-4 flex items-center gap-4 flex-wrap"
          >
            <div className={`px-3 py-2 rounded-lg text-xs font-extrabold ${mountState.has_open_job ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>
              <Wrench size={12} className="inline mr-1" />
              T{mountState.tool_no} · {mountState.has_open_job ? "In Production" : "Mounted, idle"}
            </div>
            <div className="text-xs text-slate-500 flex-1">
              <p><b>Mounted at:</b> {new Date(mountState.mounted_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}</p>
              <p><b>By:</b> {mountState.mounted_by_name || mountState.mounted_by || "—"}</p>
              {mountState.setup_checksheet_id && (
                <p>
                  <b>Setup Checksheet:</b>{" "}
                  <button
                    onClick={() => navigate(`/setup-checksheet/view/${mountState.setup_checksheet_id}`)}
                    className="text-[#1A3560] hover:underline"
                  >
                    {mountState.setup_checksheet_id}
                  </button>
                </p>
              )}
            </div>
            {!mountState.has_open_job && (
              <button
                data-testid="mount-remove-btn"
                onClick={() => setShowRemoveModal(true)}
                className="text-xs font-extrabold text-white bg-[#DC2626] hover:bg-red-700 px-3 py-2 rounded-lg inline-flex items-center gap-1.5"
              >
                <Wrench size={12} /> Remove Tool
              </button>
            )}
          </div>
        ) : (
          <div
            data-testid="mount-banner-empty"
            className="bg-slate-50 border border-slate-200 rounded-2xl p-3 mb-4 text-xs text-slate-500 inline-flex items-center gap-2"
          >
            <Wrench size={12} /> No tool currently mounted on {machineCode}.
          </div>
        )}

        {showRemoveModal && (
          <RemoveToolModal
            machineCode={machineCode}
            toolNo={mountState?.tool_no}
            onClose={() => setShowRemoveModal(false)}
            onDone={(msg) => {
              setToast(msg);
              setShowRemoveModal(false);
              setTimeout(() => setToast(""), 3500);
              fetchMount();
            }}
          />
        )}

        {loading ? (
          <div className="text-center text-slate-400 py-16">Loading...</div>
        ) : data && (
          <>
            {/* KPIs */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-4">
              <KpiCard label="OK Parts" value={data.totals.ok_qty} icon={<CheckCircle2 size={16} />} color="emerald" />
              <KpiCard label="NG Parts" value={data.totals.ng_qty} icon={<XCircle size={16} />} color="red" />
              <KpiCard label="Rejection %" value={`${rejPct}%`} color={rejPct > 5 ? "red" : rejPct > 2 ? "amber" : "emerald"} icon={<XCircle size={16} />} />
              <KpiCard label="Strokes" value={data.totals.strokes} icon={<Zap size={16} />} color="slate" />
              <KpiCard
                label="Setup Strokes"
                value={data.totals.setup_strokes_total}
                icon={<Settings size={16} />}
                color="amber"
                sub={
                  data.totals.rolled_in_strokes_total > 0
                    ? `${data.totals.rolled_in_strokes_total} rolled into jobs`
                    : (data.totals.setup_strokes_active > 0 ? `${data.totals.setup_strokes_active} unmapped` : null)
                }
              />
              <KpiCard label="Downtime" value={`${data.totals.downtime_minutes}m`} icon={<Clock size={16} />} color="red" sub={`${data.totals.downtime_events} events`} />
            </div>

            {/* Charts: Strokes/OK/NG per hour + Downtime overlay */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
              <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-4">
                <p className="text-xs font-extrabold uppercase tracking-widest text-slate-400 mb-2">
                  Strokes / OK / NG per hour
                </p>
                <ResponsiveContainer width="100%" height={280}>
                  <ComposedChart data={data.hourly} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="hour" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Bar dataKey="ok_qty" name="OK" stackId="parts" fill="#15803D" />
                    <Bar dataKey="ng_qty" name="NG" stackId="parts" fill="#DC2626" />
                    <Line type="monotone" dataKey="strokes" name="Strokes" stroke="#1A3560" strokeWidth={2} dot={{ r: 3 }} />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>

              <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-4">
                <p className="text-xs font-extrabold uppercase tracking-widest text-slate-400 mb-2">
                  Downtime &amp; Setup strokes per hour
                </p>
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={data.hourly} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="hour" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Bar dataKey="downtime_mins" name="Downtime (min)" fill="#DC2626" />
                    <Bar dataKey="setup_strokes" name="Setup strokes" fill="#D97706" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Per-stroke timeline */}
            <StrokeTimelineCard
              events={strokeEvents}
              meta={strokeMeta}
              loading={strokeLoading}
              window={strokeWindow}
              setWindow={setStrokeWindow}
              onRefresh={fetchStrokeEvents}
            />

            {/* Jobs table */}
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden mb-4">
              <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
                <p className="text-xs font-extrabold uppercase tracking-widest text-slate-500">
                  Jobs on this machine
                </p>
                <p className="text-xs text-slate-400">{data.jobs.length} job(s)</p>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-xs uppercase text-slate-500 border-b border-slate-200">
                    <tr>
                      <th className="px-4 py-2 text-left">Job</th>
                      <th className="px-4 py-2 text-left">Tool / Part</th>
                      <th className="px-4 py-2 text-left">Operator</th>
                      <th className="px-4 py-2 text-left">Batch</th>
                      <th className="px-4 py-2 text-left">Started</th>
                      <th className="px-4 py-2 text-left">Ended</th>
                      <th className="px-4 py-2 text-right">Strokes</th>
                      <th className="px-4 py-2 text-right">OK</th>
                      <th className="px-4 py-2 text-right">NG</th>
                      <th className="px-4 py-2 text-left">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.jobs.length === 0 ? (
                      <tr><td colSpan={10} className="text-center text-slate-400 py-6">No production jobs on this machine today</td></tr>
                    ) : data.jobs.map((j) => (
                      <tr key={j.job_id} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="px-4 py-2 font-mono text-xs font-bold text-slate-900">{j.job_id}</td>
                        <td className="px-4 py-2">
                          <div className="inline-flex items-center gap-1 text-xs">
                            <Wrench size={11} className="text-slate-400" /> T{j.tool_no}
                          </div>
                          <div className="text-xs text-slate-500 truncate max-w-[180px]">{j.part_name}</div>
                        </td>
                        <td className="px-4 py-2 text-xs">
                          <span className="inline-flex items-center gap-1"><User size={11} className="text-slate-400" /> {j.operator_name}</span>
                        </td>
                        <td className="px-4 py-2 text-xs text-slate-500">{j.batch_code || "\u2014"}</td>
                        <td className="px-4 py-2 text-xs font-mono text-slate-500">
                          {new Date(j.start_at).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}
                        </td>
                        <td className="px-4 py-2 text-xs font-mono text-slate-500">
                          {j.status === "OPEN" ? "—" : new Date(j.end_at).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}
                        </td>
                        <td className="px-4 py-2 text-right">
                          <div className="inline-flex items-center gap-1">
                            <span>{j.strokes}</span>
                            {j.rolled_in_setup_strokes > 0 && (
                              <span
                                data-testid={`rolled-in-badge-${j.job_id}`}
                                className="text-[9px] font-extrabold text-amber-800 bg-amber-100 border border-amber-300 px-1.5 py-0.5 rounded-full"
                                title={`${j.rolled_in_setup_strokes} pre-mapping stroke(s) rolled into this job on Stage-1 create`}
                              >
                                +{j.rolled_in_setup_strokes} rolled-in
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-2 text-right font-extrabold text-emerald-700">{j.ok_qty}</td>
                        <td className="px-4 py-2 text-right font-extrabold text-red-600">{j.ng_qty}</td>
                        <td className="px-4 py-2">
                          <span className={`text-[10px] font-extrabold px-2 py-0.5 rounded-full ${
                            j.status === "OPEN"
                              ? "bg-emerald-100 text-emerald-700 border border-emerald-200"
                              : "bg-slate-100 text-slate-600 border border-slate-200"
                          }`}>
                            {j.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Downtime events + Setup sessions */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
                <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
                  <p className="text-xs font-extrabold uppercase tracking-widest text-slate-500">
                    <Activity size={12} className="inline text-red-500" /> Downtime events ({data.downtime_events.length})
                  </p>
                </div>
                {data.downtime_events.length === 0 ? (
                  <div className="text-center text-slate-400 py-6 text-sm">No downtime events on this date</div>
                ) : (
                  <ul className="divide-y divide-slate-100">
                    {data.downtime_events.map((e) => (
                      <li key={e.event_id} className="px-4 py-2 hover:bg-slate-50">
                        <div className="flex items-center justify-between">
                          <div>
                            <b className="text-red-600 text-sm">{e.duration_mins} min</b>
                            <span className="text-xs text-slate-500 ml-2">from {e.gap_start_display || (e.gap_start || "").slice(11, 16)}</span>
                          </div>
                          <span className={`text-[10px] font-extrabold px-2 py-0.5 rounded-full ${
                            e.status === "RESOLVED"
                              ? "bg-slate-100 text-slate-600 border border-slate-200"
                              : "bg-red-100 text-red-700 border border-red-200"
                          }`}>
                            {e.status}
                          </span>
                        </div>
                        {e.reason_label && (
                          <div className="text-xs text-slate-600 mt-0.5">
                            <b>{e.reason_label || e.reason_code}:</b> {e.remark || <span className="text-slate-300">no remark</span>}
                          </div>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
                <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
                  <p className="text-xs font-extrabold uppercase tracking-widest text-slate-500">
                    <Layers size={12} className="inline text-amber-600" /> Setup sessions ({data.setup_sessions.length})
                  </p>
                  {data.totals.setup_strokes_active > 0 && (
                    <span className="text-[10px] font-extrabold text-amber-700 bg-amber-100 border border-amber-200 px-2 py-0.5 rounded-full">
                      {data.totals.setup_strokes_active} unmapped now
                    </span>
                  )}
                </div>
                {data.setup_sessions.length === 0 ? (
                  <div className="text-center text-slate-400 py-6 text-sm">No classified setup sessions today</div>
                ) : (
                  <ul className="divide-y divide-slate-100">
                    {data.setup_sessions.map((s) => (
                      <li key={s.session_id} className="px-4 py-2 hover:bg-slate-50">
                        <div className="flex items-center justify-between">
                          <div>
                            <b className="text-amber-700 text-sm">{s.strokes} strokes</b>
                            <span className="text-xs text-slate-500 ml-2">at {new Date(s.logged_at).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}</span>
                          </div>
                          <span className="text-[10px] font-extrabold text-amber-800 bg-amber-100 border border-amber-200 px-2 py-0.5 rounded-full">
                            {s.reason_label}
                          </span>
                        </div>
                        {s.remark && <p className="text-xs text-slate-500 mt-0.5 italic">{s.remark}</p>}
                        <p className="text-[10px] text-slate-400 mt-0.5">by {s.logged_by_name || s.logged_by}</p>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>

            {/* Tool mount history on this machine */}
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden mt-4">
              <div className="px-4 py-3 border-b border-slate-200">
                <p className="text-xs font-extrabold uppercase tracking-widest text-slate-500">
                  <Wrench size={12} className="inline text-slate-500" /> Tool mount history ({mountEvents.length})
                </p>
              </div>
              {mountEvents.length === 0 ? (
                <div className="text-center text-slate-400 py-6 text-sm">No mount/unmount events yet</div>
              ) : (
                <ul className="divide-y divide-slate-100 max-h-60 overflow-y-auto">
                  {mountEvents.map((e) => (
                    <li key={e.event_id} className="px-4 py-2 hover:bg-slate-50">
                      <div className="flex items-center justify-between flex-wrap gap-1">
                        <div className="text-sm">
                          <b className={e.event_type === "mounted" ? "text-emerald-700" : "text-red-700"}>
                            {e.event_type === "mounted" ? "MOUNTED" : "UN-MOUNTED"}
                          </b>
                          <span className="text-slate-500 ml-2">T{e.tool_no}</span>
                          {e.reason_label && <span className="text-[10px] font-extrabold bg-slate-100 text-slate-700 rounded-full px-2 py-0.5 ml-2">{e.reason_label}</span>}
                        </div>
                        <span className="text-[11px] font-mono text-slate-400">
                          {new Date(e.event_time).toLocaleString("en-IN", { dateStyle: "short", timeStyle: "short" })}
                        </span>
                      </div>
                      {e.remark && <p className="text-xs text-slate-500 mt-0.5 italic">{e.remark}</p>}
                      <p className="text-[10px] text-slate-400 mt-0.5">by {e.actor_name || e.actor}</p>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const RemoveToolModal = ({ machineCode, toolNo, onClose, onDone }) => {
  const [reason, setReason] = useState("production_end");
  const [remark, setRemark] = useState("");
  const [effectiveDate, setEffectiveDate] = useState(todayIso());
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async () => {
    setSubmitting(true);
    setError("");
    try {
      await api.post(`/tool-mount/${machineCode}/unmount`, {
        reason,
        remark,
        effective_date: effectiveDate,
      });
      onDone(`Tool T${toolNo} removed from ${machineCode} (${reason.replace(/_/g, " ")})`);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to remove tool");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="remove-tool-modal">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full" onClick={(e) => e.stopPropagation()}>
        <div className="bg-[#1A3560] text-white px-5 py-4 rounded-t-2xl">
          <p className="text-[10px] font-bold uppercase tracking-widest opacity-80">Remove Tool</p>
          <p className="font-extrabold text-lg">T{toolNo} from {machineCode}</p>
        </div>
        <div className="p-5 space-y-3">
          {error && <div className="bg-red-50 border border-red-200 text-red-700 text-xs px-3 py-2 rounded-lg">{error}</div>}
          <label className="block text-xs font-bold text-slate-700">
            Reason
            <select
              data-testid="remove-tool-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="mt-1 w-full border-slate-300 rounded-lg text-sm px-2 py-2"
            >
              <option value="production_end">Production End</option>
              <option value="preventive_maintenance">Preventive Maintenance</option>
              <option value="breakdown">Breakdown</option>
            </select>
          </label>
          <label className="block text-xs font-bold text-slate-700">
            Effective Date <span className="text-[10px] font-normal text-slate-400">(date the tool was physically removed)</span>
            <input
              data-testid="remove-tool-date"
              type="date"
              value={effectiveDate}
              onChange={(e) => setEffectiveDate(e.target.value)}
              className="mt-1 w-full border-slate-300 rounded-lg text-sm px-2 py-2"
            />
          </label>
          <label className="block text-xs font-bold text-slate-700">
            Remark
            <textarea
              data-testid="remove-tool-remark"
              rows={2}
              value={remark}
              onChange={(e) => setRemark(e.target.value)}
              placeholder="Describe cause / action taken"
              className="mt-1 w-full border-slate-300 rounded-lg text-sm px-2 py-2"
            />
          </label>
          <p className="text-[10px] text-slate-500 leading-snug">
            PM / Breakdown removals are automatically logged on the Tool History card.
          </p>
        </div>
        <div className="border-t border-slate-100 px-5 py-3 flex justify-end gap-2">
          <button onClick={onClose} className="text-sm font-bold text-slate-600 px-3 py-2 hover:bg-slate-100 rounded-lg">Cancel</button>
          <button
            data-testid="remove-tool-submit"
            onClick={handleSubmit}
            disabled={submitting}
            className="text-sm font-extrabold text-white bg-[#DC2626] hover:bg-red-700 disabled:bg-slate-300 px-4 py-2 rounded-lg"
          >
            {submitting ? "Removing…" : "Confirm Removal"}
          </button>
        </div>
      </div>
    </div>
  );
};

const KpiCard = ({ label, value, icon, color, sub }) => {
  const map = {
    emerald: "text-emerald-700 bg-emerald-100",
    red: "text-red-700 bg-red-100",
    amber: "text-amber-700 bg-amber-100",
    slate: "text-slate-700 bg-slate-100",
  };
  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-3">
      <div className="flex items-center justify-between">
        <p className="text-xs font-bold uppercase tracking-widest text-slate-400">{label}</p>
        {icon && (
          <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${map[color] || map.slate}`}>{icon}</div>
        )}
      </div>
      <p className="text-2xl font-extrabold text-slate-900 truncate">{value}</p>
      {sub && <p className="text-xs text-slate-400">{sub}</p>}
    </div>
  );
};

export default MachineDeepDive;

// ----- Per-stroke timeline card -----
const WINDOW_LABELS = {
  shift: "Current shift (8h)",
  "1h": "Last 1 hour",
  "24h": "Last 24 hours",
  "7d": "Last 7 days",
};

const fmtClock = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  return d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", second: "2-digit" });
};

const fmtGap = (ms) => {
  if (ms == null || !isFinite(ms)) return "—";
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  const r = m % 60;
  return r ? `${h}h ${r}m` : `${h}h`;
};

const StrokeTimelineCard = ({ events, meta, loading, window: win, setWindow, onRefresh }) => {
  // Build scatter points: production rows at y=0, setup rows at y=1
  const points = events.map((e) => ({
    t: new Date(e.stroke_time).getTime(),
    y: e.is_setup ? 1 : 0,
    is_setup: e.is_setup,
    job_id: e.job_id,
    stroke_time: e.stroke_time,
  }));
  const prodPoints = points.filter((p) => !p.is_setup);
  const setupPoints = points.filter((p) => p.is_setup);

  // Idle-gap stats across all strokes (production or setup)
  let maxGap = 0, maxGapStart = null, maxGapEnd = null;
  const sorted = [...points].sort((a, b) => a.t - b.t);
  for (let i = 1; i < sorted.length; i += 1) {
    const gap = sorted[i].t - sorted[i - 1].t;
    if (gap > maxGap) {
      maxGap = gap;
      maxGapStart = sorted[i - 1].stroke_time;
      maxGapEnd = sorted[i].stroke_time;
    }
  }

  // Compute X-axis domain to anchor to the requested window, not just event span
  const domainStart = meta.start_time ? new Date(meta.start_time).getTime() : (sorted[0]?.t || Date.now());
  const domainEnd = meta.end_time ? new Date(meta.end_time).getTime() : (sorted[sorted.length - 1]?.t || Date.now());

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-slate-200 mb-4" data-testid="per-stroke-timeline">
      <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="text-xs font-extrabold uppercase tracking-widest text-slate-500 inline-flex items-center gap-1.5">
            <Radio size={13} /> Per-stroke timeline
          </p>
          <p className="text-[11px] text-slate-400 mt-0.5">
            One tick per raw stroke · spot idle gaps & unusual clusters at a glance
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {["shift", "1h", "24h", "7d"].map((w) => (
            <button
              key={w}
              data-testid={`stroke-window-${w}`}
              onClick={() => setWindow(w)}
              className={`text-[11px] font-extrabold px-2.5 py-1 rounded-lg border transition-colors ${
                win === w
                  ? "bg-[#1A3560] text-white border-[#1A3560]"
                  : "bg-white text-slate-600 border-slate-300 hover:bg-slate-50"
              }`}
            >
              {w === "shift" ? "Shift" : w.toUpperCase()}
            </button>
          ))}
          <button
            data-testid="stroke-timeline-refresh"
            onClick={onRefresh}
            disabled={loading}
            className="text-[11px] font-bold text-slate-600 border border-slate-300 bg-white hover:bg-slate-50 px-2.5 py-1 rounded-lg disabled:opacity-50"
          >
            {loading ? "Loading…" : "Refresh"}
          </button>
        </div>
      </div>

      <div className="px-4 py-3 grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
        <StrokeStat label="Strokes" value={meta.count} tone="slate" />
        <StrokeStat label="Production" value={prodPoints.length} tone="emerald" />
        <StrokeStat label="Setup" value={setupPoints.length} tone="amber" />
        <StrokeStat label="Longest gap" value={maxGap > 0 ? fmtGap(maxGap) : "—"} tone="red"
          sub={maxGap > 0 ? `${fmtClock(maxGapStart)} → ${fmtClock(maxGapEnd)}` : null} />
      </div>

      <div className="px-2 pb-3">
        {meta.truncated && (
          <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2 py-1 mx-2 mb-2">
            Showing the latest 5,000 strokes in this window — older strokes were omitted.
          </p>
        )}
        {points.length === 0 ? (
          <div className="h-[180px] flex items-center justify-center text-slate-400 text-sm">
            {loading ? "Loading strokes…" : "No strokes recorded in this window."}
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={180}>
            <ScatterChart margin={{ top: 10, right: 20, left: 10, bottom: 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis
                dataKey="t"
                type="number"
                domain={[domainStart, domainEnd]}
                tickFormatter={(v) => fmtClock(new Date(v).toISOString())}
                tick={{ fontSize: 10 }}
                scale="time"
              />
              <YAxis
                dataKey="y"
                type="number"
                domain={[-0.5, 1.5]}
                ticks={[0, 1]}
                tickFormatter={(v) => (v === 1 ? "Setup" : "Prod")}
                tick={{ fontSize: 10 }}
                width={48}
              />
              <ZAxis range={[20, 20]} />
              <Tooltip
                cursor={{ strokeDasharray: "3 3" }}
                content={({ active, payload }) => {
                  if (!active || !payload || !payload.length) return null;
                  const p = payload[0].payload;
                  return (
                    <div className="bg-white border border-slate-200 rounded-md px-2 py-1 text-[11px] shadow-sm">
                      <div className="font-bold text-slate-800">{fmtClock(p.stroke_time)}</div>
                      <div className="text-slate-500">{p.is_setup ? "Setup" : "Production"}</div>
                      {p.job_id && <div className="text-slate-500 font-mono">{p.job_id}</div>}
                    </div>
                  );
                }}
              />
              <Scatter name="Production" data={prodPoints} fill="#15803D" shape="circle" />
              <Scatter name="Setup" data={setupPoints} fill="#D97706" shape="triangle" />
              <Legend wrapperStyle={{ fontSize: 11 }} />
            </ScatterChart>
          </ResponsiveContainer>
        )}
        <p className="text-[10px] text-slate-400 px-2 pt-1">
          Window: {WINDOW_LABELS[win]} · {fmtClock(meta.start_time)} → {fmtClock(meta.end_time)}
        </p>
      </div>
    </div>
  );
};

const StrokeStat = ({ label, value, tone = "slate", sub }) => {
  const map = {
    slate: "bg-slate-50 text-slate-700 border-slate-200",
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
    amber: "bg-amber-50 text-amber-800 border-amber-200",
    red: "bg-red-50 text-red-700 border-red-200",
  };
  return (
    <div className={`rounded-lg border px-3 py-2 ${map[tone] || map.slate}`}>
      <p className="text-[10px] font-bold uppercase tracking-widest opacity-80">{label}</p>
      <p className="text-lg font-extrabold">{value}</p>
      {sub && <p className="text-[10px] text-slate-500 truncate" title={sub}>{sub}</p>}
    </div>
  );
};
